import {
  SendWorkflowNotificationEmailRequest,
  SmtpServer,
  WorkflowNotificationResource,
  WorkflowsService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock9, Loader2, Mail, Send } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

export interface Email {
  id: string;
  threadId?: string | null;
  from: string;
  subject: string;
  date: string;
  body: string;
  read: boolean;
  smtpServer?: SmtpServer;
  messages?: Email[];
  resources?: WorkflowNotificationResource[];
  nodeId?: string;
  workflowId?: string;
  organizationId?: string;
}

export interface EmailDetailProps {
  email: Email | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EmailDetail: React.FC<EmailDetailProps> = ({
  email,
  open,
  onOpenChange,
}) => {
  if (!email) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center text-gray-500"></div>
    );
  }

  // If this is a thread, use all messages; otherwise, a single message.
  const conversation =
    email.messages && email.messages.length > 0 ? email.messages : [email];

  // Sort conversation (oldest at the top)
  const sortedConversation = conversation.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const org = useStore($org)?.id;
  const queryClient = useQueryClient();
  const [reply, setReply] = React.useState('');

  const sendEmail = useMutation({
    mutationFn: async (payload: SendWorkflowNotificationEmailRequest) => {
      // call the api to create the organization
      const res = await WorkflowsService.sendWorkflowNotificationEmail(payload);
      return res;
    },
    onSuccess: async data => {
      // invalidate queries here so that they are refetched
      await queryClient.invalidateQueries({
        queryKey: ['getNotifications', org, undefined, 'SMS_NOTIFICATION'],
      });
      setReply('');
      toast.success('Message sent successfully.');
    },
    onError: error => {
      toast.error('Failed to send message.');
    },
  });

  console.log('lalala', sortedConversation);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="flex max-h-[90vh] max-w-[650px] flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="mr-2">
                <Mail className="h-5 w-5 text-blue-500" />
              </div>
              {email.subject}
            </div>
            <div className="flex items-center">
              {email.smtpServer && email.smtpServer === 'OUTLOOK' ? (
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-6 h-7 w-7"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#03A9F4"
                      d="M21,31c0,1.104,0.896,2,2,2h17c1.104,0,2-0.896,2-2V16c0-1.104-0.896-2-2-2H23c-1.104,0-2,0.896-2,2V31z"
                    ></path>
                    <path
                      fill="#B3E5FC"
                      d="M42,16.975V16c0-0.428-0.137-0.823-0.367-1.148l-11.264,6.932l-7.542-4.656L22.125,19l8.459,5L42,16.975z"
                    ></path>
                    <path
                      fill="#0277BD"
                      d="M27 41.46L6 37.46 6 9.46 27 5.46z"
                    ></path>
                    <path
                      fill="#FFF"
                      d="M21.216,18.311c-1.098-1.275-2.546-1.913-4.328-1.913c-1.892,0-3.408,0.669-4.554,2.003c-1.144,1.337-1.719,3.088-1.719,5.246c0,2.045,0.564,3.714,1.69,4.986c1.126,1.273,2.592,1.91,4.378,1.91c1.84,0,3.331-0.652,4.474-1.975c1.143-1.313,1.712-3.043,1.712-5.199C22.869,21.281,22.318,19.595,21.216,18.311z M19.049,26.735c-0.568,0.769-1.339,1.152-2.313,1.152c-0.939,0-1.699-0.394-2.285-1.187c-0.581-0.785-0.87-1.861-0.87-3.211c0-1.336,0.289-2.414,0.87-3.225c0.586-0.81,1.368-1.211,2.355-1.211c0.962,0,1.718,0.393,2.267,1.178c0.555,0.795,0.833,1.895,0.833,3.31C19.907,24.906,19.618,25.968,19.049,26.735z"
                    ></path>
                  </svg>
                </div>
              ) : (
                <div className="flex items-center">
                  <svg
                    aria-hidden="true"
                    className="mr-6 h-6 w-6"
                    viewBox="0 0 48 48"
                  >
                    <path
                      fill="#FFC107"
                      d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                    ></path>
                    <path
                      fill="#FF3D00"
                      d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                    ></path>
                    <path
                      fill="#4CAF50"
                      d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                    ></path>
                    <path
                      fill="#1976D2"
                      d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
          </DialogTitle>
          <DialogDescription>
            <div className="mt-1 flex items-center text-xs text-gray-500">
              <Clock9 className="mr-1 h-3 w-3" />
              {new Date(email.date).toLocaleDateString()}{' '}
              {new Date(email.date).toLocaleTimeString()}
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="flex h-[100%] flex-col overflow-y-auto overflow-x-hidden px-2">
          {/* Conversation Thread */}

          {sortedConversation.map((msg, index) => (
            <div key={msg.id} className="mb-3 rounded-md border">
              <div className="rounded-lg bg-white p-4 shadow-sm">
                <div className="mb-4">
                  <div className="flex flex-col space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center justify-between gap-x-1">
                      <div>
                        <span className="font-medium">From</span>
                        <span className="ml-1 max-w-[100px] break-words">
                          {msg.from}
                        </span>
                      </div>

                      <div className="flex items-center whitespace-nowrap text-xs text-gray-500">
                        <Clock9 className="mr-1 h-3 w-3" />
                        {new Date(msg.date).toLocaleDateString()}{' '}
                        {new Date(msg.date).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
                <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-gray-800">
                  {msg.body}
                </p>

                {msg.resources && msg.resources.length > 0 && (
                  <div className="mt-4 flex flex-col gap-2">
                    <p className="text-sm font-medium text-gray-600">
                      Attachments:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {msg.resources.map((resource, index) => (
                        <a
                          key={index}
                          href={resource.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-600 transition-colors hover:bg-blue-100 hover:text-blue-800"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                          {resource.fileName}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex w-full">
          <div className="flex w-full flex-col gap-2">
            <Textarea
              placeholder="Type your message here."
              rows={3}
              className="w-full"
              value={reply}
              onChange={e => setReply(e.target.value)}
              disabled={sendEmail.isPending}
            />

            <div className="flex justify-end">
              {/* <TooltipProvider delayDuration={1}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button className="" variant="outline" size="sm">
                  <Sparkles className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="bg-slate-900">
                <p>Generate reply with AI</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}

              <Button
                variant="default"
                size="sm"
                disabled={sendEmail.isPending}
                onClick={async () => {
                  if (
                    !sortedConversation[sortedConversation?.length - 1].threadId
                  ) {
                    return toast.error('Thread ID is empty.');
                  }
                  await sendEmail.mutateAsync({
                    threadId:
                      sortedConversation[sortedConversation?.length - 1]
                        .threadId!,
                    messageBody: reply,
                    workflowNodeId:
                      sortedConversation[sortedConversation?.length - 1]
                        .nodeId!, // asuming this will always be present
                    lastNotificationId:
                      sortedConversation[sortedConversation?.length - 1].id,
                    organizationId: org!,
                  });
                }}
              >
                {sendEmail.isPending ? (
                  <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                ) : (
                  <Send className="mr-2 h-3 w-3" />
                )}
                Send
              </Button>
            </div>
          </div>

          <div className="mx-2 mb-2 flex justify-end gap-2"></div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmailDetail;
